import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

const API_BASE_URL = "https://www.lankastudio.cn/api";
export const MANUAL_VIEW_BASE = "https://www.lankastudio.cn/efficiencyServiceCenter/manual-view";
const POSTER_EMBED_BASE = "https://www.lankastudio.cn/efficiencyServiceCenter/embed/poster";

/**
 * 产品详情中的「海报 / 说明书」入口
 * V2 海报优先用组件树实时嵌入预览，避免展示错位的旧成品 PNG。
 */
export default function ProductDocsSection({ product, compact = false }) {
  const [manuals, setManuals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [posterOpen, setPosterOpen] = useState(false);
  const [embedFailed, setEmbedFailed] = useState(false);

  const productId = product?.id;
  const posterId = product?.poster_id || null;
  const posterUrl = product?.poster_image_url || null;
  const hasPoster = !!(posterId || posterUrl);

  const fitWidth = useMemo(() => {
    if (typeof window === "undefined") return 720;
    return Math.min(720, Math.max(280, window.innerWidth - 48));
  }, [posterOpen]);

  const embedSrc = posterId
    ? `${POSTER_EMBED_BASE}/${posterId}?region=full&fit=${fitWidth}&bg=%23111111`
    : null;

  useEffect(() => {
    if (!productId) {
      setManuals([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE_URL}/manuals?product_id=${productId}`);
        if (!res.ok) throw new Error("load manuals failed");
        const data = await res.json();
        if (!cancelled) setManuals(Array.isArray(data) ? data : []);
      } catch (_) {
        if (!cancelled) setManuals([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [productId]);

  useEffect(() => {
    if (!posterOpen) setEmbedFailed(false);
  }, [posterOpen]);

  if (!hasPoster && !loading && manuals.length === 0) return null;

  const manualTitle = (m) => {
    const t = m.title || {};
    return t.chineseName || t.englishName || `说明书 #${m.id}`;
  };

  const useLiveEmbed = !!(embedSrc && !embedFailed);
  const embedHeight = Math.round(fitWidth * (2114 / 1694));

  return (
    <div style={{ marginTop: compact ? 12 : 20, marginBottom: compact ? 8 : 16 }}>
      <h4
        style={{
          margin: "0 0 10px",
          fontSize: compact ? 14 : 15,
          color: "#5c4033",
          fontWeight: 700,
        }}
      >
        资料与说明书
      </h4>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {hasPoster && (
          <button
            type="button"
            onClick={() => setPosterOpen(true)}
            style={actionBtn}
          >
            查看海报
          </button>
        )}
        {manuals.map((m) => (
          <Link
            key={m.id}
            to={`/manuals/${m.id}`}
            style={{ ...actionBtn, textDecoration: "none", display: "inline-flex", alignItems: "center" }}
          >
            查看说明书
            {manuals.length > 1 ? ` · ${manualTitle(m)}` : ""}
          </Link>
        ))}
        {!loading && manuals.length === 0 && hasPoster ? (
          <span style={{ fontSize: 12, color: "#9a8a7a", alignSelf: "center" }}>暂无说明书</span>
        ) : null}
        {loading ? <span style={{ fontSize: 12, color: "#9a8a7a" }}>加载资料…</span> : null}
      </div>

      {posterOpen && (useLiveEmbed || posterUrl) ? (
        <div
          role="dialog"
          style={lightboxOverlay}
          onClick={() => setPosterOpen(false)}
        >
          <button type="button" style={lightboxClose} onClick={() => setPosterOpen(false)}>
            ×
          </button>
          {useLiveEmbed ? (
            <iframe
              title="产品海报"
              src={embedSrc}
              style={{
                ...lightboxFrame,
                width: fitWidth,
                height: Math.min(embedHeight, typeof window !== "undefined" ? window.innerHeight * 0.92 : embedHeight),
              }}
              onClick={(e) => e.stopPropagation()}
              onError={() => setEmbedFailed(true)}
            />
          ) : (
            <img
              src={posterUrl}
              alt="产品海报"
              style={lightboxImg}
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>
      ) : null}
    </div>
  );
}

const actionBtn = {
  padding: "8px 14px",
  borderRadius: 8,
  border: "1px solid #d6c8b4",
  background: "#fffaf3",
  color: "#5c4033",
  fontWeight: 700,
  fontSize: 13,
  cursor: "pointer",
};

const lightboxOverlay = {
  position: "fixed",
  inset: 0,
  zIndex: 9999,
  background: "rgba(0,0,0,0.82)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 16,
};

const lightboxClose = {
  position: "absolute",
  top: 12,
  right: 16,
  width: 40,
  height: 40,
  border: "none",
  borderRadius: 20,
  background: "rgba(255,255,255,0.15)",
  color: "#fff",
  fontSize: 28,
  lineHeight: 1,
  cursor: "pointer",
};

const lightboxImg = {
  maxWidth: "min(960px, 100%)",
  maxHeight: "92vh",
  objectFit: "contain",
  borderRadius: 8,
  boxShadow: "0 12px 40px rgba(0,0,0,0.45)",
};

const lightboxFrame = {
  border: "none",
  borderRadius: 8,
  background: "#111",
  boxShadow: "0 12px 40px rgba(0,0,0,0.45)",
  maxHeight: "92vh",
};
